import { useMemo } from "react";

import type { Direction } from "../contract/types";
import { useBackend } from "../app/BackendProvider";
import { describeError, useResource } from "../app/useResource";
import { useNetworkId } from "../config/ConfigProvider";
import { LineBadge } from "../cockpit/bits";

/**
 * Itinéraires de régulation (F3.4, CDC §5.2). Trajets nommés prédéfinis entre
 * carrefours, rattachés à une ligne, utilisés comme référence commune par les
 * régulateurs (« fais du carrefour X au carrefour Y »). Gérés en
 * administration (B7.1) — ici, lecture seule. `junctionCodes` est le parcours
 * COMPLET dans l'ordre, pas seulement les deux extrémités : un itinéraire
 * réel peut traverser plusieurs carrefours intermédiaires.
 */
export function RegRoutesView() {
  const backend = useBackend();
  const networkId = useNetworkId();

  const routesRes = useResource((signal) => backend.api.listRegRoutes(networkId, {}, signal), [backend, networkId]);
  const junctionsRes = useResource((signal) => backend.api.listJunctions(networkId, {}, signal), [backend, networkId]);

  const junctionName = useMemo(() => {
    const map = new Map((junctionsRes.data ?? []).map((j) => [j.code, j.name]));
    return (code: string) => map.get(code) ?? code;
  }, [junctionsRes.data]);

  const error = routesRes.error ?? junctionsRes.error;

  return (
    <div>
      <h2 className="ovl" style={{ marginBottom: 12 }}>Itinéraires de régulation</h2>

      {routesRes.loading && !routesRes.data ? (
        <p className="has-text-grey is-size-7">Chargement…</p>
      ) : error ? (
        <p className="has-text-danger is-size-7">{describeError(error)}</p>
      ) : (routesRes.data?.length ?? 0) === 0 ? (
        <p className="has-text-grey is-size-7">Aucun itinéraire défini.</p>
      ) : (
        <div style={{ overflowX: "auto" }}>
        <table className="table is-fullwidth is-narrow is-striped" style={{ fontSize: "0.78rem" }}>
          <thead>
            <tr>
              <th className="ovl" style={{ width: 60 }}>Ligne</th>
              <th className="ovl">Itinéraire</th>
              <th className="ovl" style={{ width: 70 }}>Sens</th>
              <th className="ovl">Parcours</th>
            </tr>
          </thead>
          <tbody>
            {(routesRes.data ?? []).map((route) => (
              <tr key={route.id}>
                <td>
                  <LineBadge lineId={route.lineId} size={22} />
                </td>
                <td style={{ fontWeight: 600 }}>{route.name}</td>
                <td className="mono has-text-grey">{directionLabel(route.direction)}</td>
                <td className="mono has-text-grey">
                  {route.junctionCodes.map((code) => `${code} · ${junctionName(code)}`).join(" → ")}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      )}
    </div>
  );
}

function directionLabel(direction: Direction | null): string {
  return direction === 0 ? "Aller" : direction === 1 ? "Retour" : "—";
}
